import { useState, useEffect } from "react";
import Twemoji from "react-twemoji";
import "./UnlockPage.css";

import { PuzzleData, PuzzleType } from "sockapi/puzzle-data";
import { ScopeType } from "sockapi/scope-types";
import { AdminModifyTeamParams } from "sockapi/scope-specs/hunt-admin-spec";
import { SavedHuntBackendState } from "sockapi/scope-specs/state-download-spec";
import { useNavStore } from "stores/NavStore";
import {
  useSockClient,
  useTeamId,
  useShouldDisplayAdmin,
} from "stores/ServerInteractionStore";
import { useGlobalScopeState, useTeamScopeState } from "stores/ScopeStateStore";
import { Link } from "components/Link";
import { LargeSpinner, Spinner } from "components/Loading";
import { Paper } from "components/Material";
import Tooltip from "components/Tooltip";
import {
  PuzzleFilter,
  emptyPuzzleFilter,
  checkPuzzleFilter,
  AdminTopControl,
  AdminPuzzleControl,
  sortAdminPagePuzzles,
} from "pages/AdminPage";
import { PuzzlePageWrapper } from "pages/PuzzlePage";
import { downloadTeamStateAsync } from "client-util/util";
import { useUILock } from "client-util/useUILock";
import settings from "settings";

function UnlockPuzzlesDisplay({ filter }: { filter: PuzzleFilter }) {
  const puzzles = useGlobalScopeState(ScopeType.HUNT_ADMIN)?.puzzles ?? {};
  const teamId = useTeamId();

  if (teamId === null || Object.keys(puzzles).length === 0)
    return <LargeSpinner />;

  const puzzlesDom = sortAdminPagePuzzles(puzzles).map((puzData) => {
    if (!checkPuzzleFilter(filter, puzData)) return null;
    return (
      <AdminPuzzleControl
        key={puzData.puzName}
        teamId={teamId}
        puzName={puzData.puzName}
      />
    );
  });

  return <div className="unlock-puzzles">{puzzlesDom}</div>;
}

function StateUploader() {
  const sockClient = useSockClient();
  const [stateToUpload, setStateToUpload] =
    useState<SavedHuntBackendState | null>(null);

  if (!settings.localMode)
    return <Paper>State loader is only supported in local mode.</Paper>;

  return (
    <>
      <button
        onClick={() => {
          if (stateToUpload === null) return;
          sockClient.resetServer(stateToUpload);
          setStateToUpload(null);
        }}
        disabled={stateToUpload === null}
      >
        Load progress
      </button>{" "}
      <input
        type="file"
        onChange={(e) => {
          if (e.target.files === null) return;
          const [file] = e.target.files;
          if (file === undefined) return;
          if (file.type !== "application/json") {
            toastr.error(
              "Saved state should be a JSON file.",
              "Unable to load state"
            );
            return;
          }

          const reader = new FileReader();
          reader.onload = (e) => {
            const jsonData = e.target?.result;
            if (typeof jsonData !== "string")
              throw new Error("expect file data to be a string");
            const state = (() => {
              try {
                return JSON.parse(jsonData);
              } catch (err) {
                console.error(err);
                toastr.error("Failed to parse file.");
                return null;
              }
            })();
            if (state === null) return;
            setStateToUpload(state);
          };
          reader.onerror = (err) => {
            console.error(err);
            toastr.error("Failed to read file.");
          };
          reader.readAsText(file);
        }}
      />
    </>
  );
}

function StateDownloader() {
  const sockClient = useSockClient();
  const teamId = useTeamId();
  const downloadLock = useUILock();

  if (teamId === null) return <Spinner inline />;

  return (
    <button
      onClick={() => {
        downloadLock.acquireAndRun(async (getIsCanceled) => {
          await downloadTeamStateAsync(sockClient, teamId, getIsCanceled);
        });
      }}
      disabled={downloadLock.isHeld}
    >
      Save progress
    </button>
  );
}

function UnlockInfo({ filter }: { filter: PuzzleFilter }) {
  const teamScopeState = useTeamScopeState();
  const sockClient = useSockClient();
  const huntAdminScopeState = useGlobalScopeState(ScopeType.HUNT_ADMIN);
  const teamId = useTeamId();

  if (
    teamId === null ||
    teamScopeState === null ||
    huntAdminScopeState === null
  )
    return <LargeSpinner />;

  const { puzzles } = teamScopeState;
  const { puzzles: allPuzzles } = huntAdminScopeState;
  const filteredPuzzles = Object.values(allPuzzles).filter((puzData) =>
    checkPuzzleFilter(filter, puzData)
  );
  const solvablePuzzles = filteredPuzzles.filter(
    (puzData) => puzData.type !== PuzzleType.PIECE
  );

  const resetAll = () => {
    sockClient.resetServer();
  };

  const makePuzConstValDict = (list: PuzzleData[], val: boolean) =>
    Object.fromEntries(list.map(({ puzName }) => [puzName, val]));

  const sendModifyTeamUpdate = (upd: AdminModifyTeamParams) =>
    sockClient.sendAdminScopeStep({
      teams: {
        [teamId]: upd,
      },
    });

  const unlockAllPuzzles = () =>
    sendModifyTeamUpdate({
      overrideUnlocked: makePuzConstValDict(filteredPuzzles, true),
    });

  const lockAllPuzzles = () =>
    sendModifyTeamUpdate({
      overrideUnlocked: makePuzConstValDict(filteredPuzzles, false),
    });

  const solveAllPuzzles = () =>
    sendModifyTeamUpdate({
      overrideSolved: makePuzConstValDict(solvablePuzzles, true),
    });

  const unsolveAllPuzzles = () =>
    sendModifyTeamUpdate({
      overrideSolved: makePuzConstValDict(solvablePuzzles, false),
    });

  /*
  const getAllCannedHints = () =>
    sendModifyTeamUpdate({
      overridePuzzleNumTotalCannedHints: puzzleNumTotalCannedHints,
    });
  */

  const unlockCount = Object.values(puzzles).filter(
    (puzData) => checkPuzzleFilter(filter, puzData) && puzData.unlockTime
  ).length;
  const solveCount = Object.values(puzzles).filter(
    (puzData) => checkPuzzleFilter(filter, puzData) && puzData.solveTime
  ).length;

  return (
    <div className="unlock-toolbar">
      <div className="button-squish-zone">
        <div>
          <button onClick={resetAll}>
            <Twemoji tag="span">🔄️ Reset all progress</Twemoji>
          </button>
        </div>
        <Tooltip text="Lock all">
          <button onClick={lockAllPuzzles}>
            <Twemoji tag="span">🔒</Twemoji>
          </button>
        </Tooltip>
        <Tooltip text="Unlock all">
          <button onClick={unlockAllPuzzles}>
            <Twemoji tag="span">🔑</Twemoji>
          </button>
        </Tooltip>
        <Tooltip text="Unsolve all">
          <button onClick={unsolveAllPuzzles}>
            <Twemoji tag="span">🔲️</Twemoji>
          </button>
        </Tooltip>
        <Tooltip text="Solve all">
          <button onClick={solveAllPuzzles}>
            <Twemoji tag="span">✅️</Twemoji>
          </button>
        </Tooltip>
        {/*
        <Tooltip text="Get all canned hints">
          <button onClick={getAllCannedHints}>
            <Twemoji tag="span">🥫️</Twemoji>
          </button>
        </Tooltip>
        */}
      </div>
      <Twemoji tag="span">
        {unlockCount} / {filteredPuzzles.length} 🔑
      </Twemoji>
      <Twemoji tag="span">
        {solveCount} / {solvablePuzzles.length} ✅️
      </Twemoji>
    </div>
  );
}

function UnlockPage() {
  const teamId = useTeamId();
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  const [filter, setFilter] = useState(emptyPuzzleFilter());
  const setRedirectRoute = useNavStore((state) => state.setRedirectRoute);

  useEffect(() => {
    if (shouldDisplayAdmin) return;
    if (settings.isPosthunt) return;
    setRedirectRoute({ route: "puzzles", replace: true });
  }, [shouldDisplayAdmin, setRedirectRoute]);

  if (teamId === null) return <LargeSpinner />;

  return (
    <PuzzlePageWrapper>
      <div className="centered-page unlock-page">
        <h1>Unlocking</h1>
        <Paper seed="UnlockPage: be careful!">
          <Twemoji tag="span">⚠️</Twemoji> <b>Be careful!</b> There are no
          confirmations for anything you click on this page.
          <br />
          <br />
          <Twemoji tag="span">⚠️</Twemoji> <b>Note:</b> Setting a puzzle to
          locked may appear to fail silently if your existing solves would cause
          the puzzle to be immediately unlocked.
        </Paper>
        <div>
          <p>
            <b>To experience the hunt like a team during the hunt would</b>,
            click <Twemoji tag="span">“🔄️ Reset all progress”</Twemoji> and
            start at the <Link route="puzzles">world</Link>. You won’t need to
            use this page again, unless you want to skip puzzles you’re stuck
            on. Some details about the postsolving experience:
          </p>
          <ul>
            <li>
              All hunt features are simulated, including interactions with hunt
              staff and other teams. You will not need to manually set puzzles
              solved or unlocked to fully progress through the hunt.
            </li>
            <li>
              Progress is saved locally in your browser. On most desktop
              browsers, your progress will be synced across all your browser
              tabs.
            </li>
            <li>
              There is no support for interaction between multiple solvers.
            </li>
            <li>The leaderboard is frozen.</li>
          </ul>
          <p>
            The default initial posthunt state has all puzzles unlocked to
            support postsolving individual puzzles independently.{" "}
            <b>To restore this state</b>, click{" "}
            <Twemoji tag="span">“🔄️ Reset all progress”</Twemoji>, then{" "}
            <Twemoji tag="span">“🔑 Unlock all”</Twemoji>.
          </p>
          <p>
            You may <b>save or load your team&rsquo;s progress</b> using the
            buttons below. This lets you make backups of your progress or
            transfer your progress between browsers. You may also use this to
            transfer your team&rsquo;s progress from the live hunt website
            before the hunt closes.
          </p>
        </div>
        {shouldDisplayAdmin || settings.isPosthunt ? (
          <>
            <div className="hunt-state-control">
              <StateDownloader />
              <StateUploader />
            </div>
            <AdminTopControl
              teamId={teamId}
              filter={filter}
              setFilter={setFilter}
            />
            <UnlockInfo filter={filter} />
            <UnlockPuzzlesDisplay filter={filter} />
          </>
        ) : (
          <>
            <div>
              <StateDownloader />
            </div>
            <div>
              <p>
                You are still logged in. You will lose access to your
                team&rsquo;s progress after the hunt closes. To continue
                progressing through the hunt locally:
              </p>
              <ol>
                <li>
                  Download your team&rsquo;s progress with the button above.
                </li>
                <li>Log out.</li>
                <li>
                  Navigate back to this page and upload the file containing your
                  team&rsquo;s progress.
                </li>
              </ol>
            </div>
          </>
        )}
      </div>
    </PuzzlePageWrapper>
  );
}

export default UnlockPage;
